import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { usePutOperationMutation } from "redux/api/operationApi";
import { setCurrentOperation } from "redux/features/operationSlice";

const PurchasePurposeDropdown: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [
    updateOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      error: apiError,
    },
  ] = usePutOperationMutation();

  const [purchasePurpose, setPurchasePurpose] = useState("");
  const [customPurpose, setCustomPurpose] = useState("");
  const [fundsOriginCountry, setFundsOriginCountry] = useState("");
  const [legalFunds, setLegalFunds] = useState<boolean | null>(null);
  const [initialState, setInitialState] = useState<{ purchasePurpose: string, legalFunds: boolean | null, fundsOriginCountry: string } | null>(null);
  const [error, setError] = useState(false);

  const operation = useSelector((state: any) => {
    return state.operationState?.currentOperation?.data || state.operationState?.currentOperation;
  });

  useEffect(() => {
    if (operation) {
      const savedPurpose = operation.purchasePurpose || "";
      const predefinedPurposes = ["gift", "self-use", "business"];

      const initialPurchasePurpose = predefinedPurposes.includes(savedPurpose) ? savedPurpose : "";
      const initialCustomPurpose = predefinedPurposes.includes(savedPurpose) ? "" : savedPurpose;

      setPurchasePurpose(initialPurchasePurpose);
      setCustomPurpose(initialCustomPurpose);
      setLegalFunds(operation.legalFunds ?? null);
      setFundsOriginCountry(operation.fundsOriginCountry || "");

      setInitialState({
        purchasePurpose: initialPurchasePurpose,
        legalFunds: operation.legalFunds ?? null,
        fundsOriginCountry: operation.fundsOriginCountry || "",
      });
    }
  }, [operation]);


  if (!operation) {
    return <p>{formatMessage({ id: "purpose.error" })}</p>;
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    setPurchasePurpose(event.target.value as string);
  };
  const handleLegalFunds = (value: boolean) => {
    setLegalFunds(value);
  };
  const handleSubmit = () => {
    if (
      purchasePurpose === "" ||
      (purchasePurpose === "other" && customPurpose === "") ||
      legalFunds === null ||
      fundsOriginCountry === ""
    ) {
      setError(true);
      return;
    }
    if (initialState && purchasePurpose === initialState.purchasePurpose && legalFunds === initialState.legalFunds && fundsOriginCountry === initialState.fundsOriginCountry) {
      nextStep();
      return;
    }
    setError(false);
    const updatedOperation = {
      ...operation,
      purchasePurpose:
        purchasePurpose === "other" ? customPurpose : purchasePurpose,
      legalFunds: legalFunds,
      fundsOriginCountry: fundsOriginCountry,
    };
    const formData = new FormData();
    formData.append("data", JSON.stringify(updatedOperation));
    updateOperation({ id: operation.id, operationToUpdate: formData }).then(
      (res) => {
        if ("data" in res) {
          dispatch(setCurrentOperation(res.data));
          nextStep();
        }
      }
    );
  };
  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {formatMessage({ id: "purpose.title" })}
          </Typography>
        }
      />
      <CardContent>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "start",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <FormControl
              error={error === true}
              sx={{
                borderRadius: "2px",
                padding: "5px",
                gap: "20px",
                width: "100%",
              }}
            >
              <Select
                id="public-office-select"
                value={purchasePurpose}
                onChange={handleChange}
                displayEmpty
                sx={{
                  border: "1.5px solid black",
                  width: "100%",
                  backgroundColor: "white", // Establecer fondo blanco
                  "& .MuiSelect-root": {
                    backgroundColor: "white", // Fondo para la raíz del select
                  },
                  "&:hover": {
                    backgroundColor: "white", // Fondo al pasar el mouse
                  },
                  "&.Mui-focused": {
                    border: "1.5px solid black", // Elimina el borde rojo en el estado focused
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none", // Si usas `OutlinedInput`, asegúrate de eliminar el borde extra
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1px solid black", // Borde del menú desplegable
                      borderRadius: "5px",
                      marginTop: "4px",
                      boxShadow: "0px 4px 6px rgba(0,0,0,0.1)", // Sombra para un diseño más limpio
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <Typography sx={{ color: "rgba(0,0,0,0.6)" }}>
                    {formatMessage({ id: "purpose.select.title" })}
                  </Typography>
                </MenuItem>
                <MenuItem value={"gift"}>{formatMessage({ id: "purpose.gift" })}</MenuItem>
                <MenuItem value={"self-use"}>{formatMessage({ id: "purpose.own" })}</MenuItem>
                <MenuItem value={"business"}>{formatMessage({ id: "purpose.company" })}</MenuItem>
                <MenuItem value={"other"}>{formatMessage({ id: "purpose.other" })}</MenuItem>
              </Select>
              {/* Mostrar campo de texto si seleccionan "Otro caso" */}
              {purchasePurpose === "other" && (
                <TextField
                  placeholder={formatMessage({ id: "purpose.placeholder" })}
              value={customPurpose}
              onChange={(e) => setCustomPurpose(e.target.value)}
              variant="standard"
              InputProps={{
                sx: {
                  "&::placeholder": {
                    color: "#ccc",
                    opacity: 1,
                  },
                },
              }}
              sx={{
                width: "100%",
              }}
                />
              )}
              {/* Mostrar mensaje de error si no se ha seleccionado nada */}
              {error === true &&
                (purchasePurpose === "" ||
                  (purchasePurpose === "other" && customPurpose === "")) && (
                  <FormHelperText>{formatMessage({ id: "purpose.error.select" })}</FormHelperText>
                )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                }}
              >
                <Typography sx={{ marginBottom: "16px", textAlign: "center" }}>
                  {formatMessage({ id: "purpose.question" })}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px", // Espaciado entre los botones
                  }}
                >
                  {/* Botón "Sí" */}
                  <Button
                    onClick={() => handleLegalFunds(true)}
                    sx={{
                      border: "2px solid black",
                      backgroundColor: legalFunds === true ? "black" : "white",
                      color: legalFunds === true ? "white" : "black",
                      borderRadius: "15px",
                      padding: "10px 20px",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: legalFunds === true ? "#333" : "#f2f2f2",
                      },
                    }}
                  >
                    {formatMessage({ id: "public.office.yes" })}
                  </Button>

                  {/* Botón "No" */}
                  <Button
                    onClick={() => handleLegalFunds(false)}
                    sx={{
                      border: "2px solid black",
                      backgroundColor: legalFunds === false ? "black" : "white",
                      color: legalFunds === false ? "white" : "black",
                      borderRadius: "15px",
                      padding: "10px 20px",
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: legalFunds === false ? "#333" : "#f2f2f2",
                      },
                    }}
                  >
                    {formatMessage({ id: "public.office.no" })}
                  </Button>
                </Box>
                {error === true && legalFunds === null && (
                  <FormHelperText>
                    {formatMessage({ id: "purpose.money.procedence" })}
                  </FormHelperText>
                )}
              </Box>

              {error === true && fundsOriginCountry === "" && (
                <FormHelperText>
                  {formatMessage({ id: "purpose.country" })}                  
                </FormHelperText>
              )}
              <TextField
                placeholder={formatMessage({ id: "purpose.money.procedence" })}
                variant="standard"
                disabled={legalFunds === null}
                InputProps={{
                  sx: {
                    "&::placeholder": {
                      opacity: 1,
                    },
                  },
                }}
                value={fundsOriginCountry}
                onChange={(e) => setFundsOriginCountry(e.target.value)}
              />
            </FormControl>
          </Box>
          <Box alignItems="end">

            <Grid container spacing={2} justifyContent="space-between">
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 3,
                    backgroundColor: "black",
                    color: "white",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                  onClick={handleSubmit}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </CardContent>
    </Card >
  );
};

export default PurchasePurposeDropdown;
